import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";
import apiClient from "@/lib/api-client";
import { apiErrorDetail } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { useScoringStore } from "@/stores/scoring-store";
import type { ScoringBatchStatus } from "@/types/paper";

function scoringBatchQuery(conceptId: string) {
  return {
    queryKey: ["scoring-batch", conceptId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/scoring/batch-status`)
        .json<ScoringBatchStatus>(),
    refetchInterval: (q: { state: { data?: ScoringBatchStatus } }) =>
      q.state.data?.state === "pending" ? 20_000 : false,
    enabled: !!conceptId,
  };
}

/** Whether a durable background batch is scoring this concept. Shares the
 * status query's cache without its completion toasts, so any number of
 * readers can mount it. */
export function useBackgroundScoringPending(conceptId: string | undefined): boolean {
  return useQuery(scoringBatchQuery(conceptId ?? "")).data?.state === "pending";
}

/** Polls the durable batch job (20s while pending — FD partial-map cadence).
 * Also catches cross-session pending batches: the durable row survives
 * deploys and page reloads that the SSE stream does not.
 *
 * Guard the empty-id case (`enabled`) so mount sites that don't always have
 * a concept in scope can pass `conceptId ?? ""` without firing a bad
 * request — same convention as useFieldDebate. */
export function useScoringBatchStatus(conceptId: string) {
  const queryClient = useQueryClient();
  const query = useQuery(scoringBatchQuery(conceptId));

  const prevState = useRef<string | undefined>(undefined);
  useEffect(() => {
    const state = query.data?.state;
    // An angle rescore rides the same batch table; name it as the angle pill does.
    const angle = query.data?.mode === "angle_rescore";
    const name = angle ? "Angle score refresh" : "Background scoring";
    if (prevState.current === "pending" && state === "complete") {
      toast.success(angle ? "Angle scores refreshed" : "Background scoring complete", {
        description: `${query.data?.tail_total ?? 0} papers scored.`,
      });
      useScoringStore.getState().clearBackgroundBatch();
      void queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    } else if (prevState.current === "pending" && state === "failed") {
      // The cancel endpoints mark the row failed with error="cancelled" —
      // a user-initiated stop, not a failure. Keep the toast neutral.
      if (query.data?.error === "cancelled") {
        toast.info(`${name} cancelled`, {
          description: "Remaining papers were left unscored.",
        });
      } else {
        toast.error(`${name} failed`, {
          description: query.data?.error ?? "Re-run scoring to retry.",
          duration: 12_000,
        });
      }
      useScoringStore.getState().clearBackgroundBatch();
    }
    prevState.current = state;
  }, [query.data, conceptId, queryClient]);

  return query;
}

/** Cancels the pending durable batch job for a concept (the pill's ×).
 * The backend marks the row failed with error="cancelled"; the status
 * poll above owns the resulting toast + store cleanup, so this mutation
 * only invalidates. A 404 means the batch reached a terminal state
 * between the last poll and the click — not an error worth surfacing. */
export function useCancelScoringBatch(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient.post(`api/concepts/${conceptId}/scoring/batch/cancel`),
    onError: async (err) => {
      if (err instanceof HTTPError && err.response.status === 404) return;
      toast.error("Couldn't cancel background scoring", {
        description: await apiErrorDetail(err, "Try again."),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["scoring-batch", conceptId],
      });
    },
  });
}
